/**
 * Side panel pour afficher les messages email et gérer les confirmations
 * Design cohérent avec le reste de l'application
 */
import { useState, useEffect } from 'react'
import { X, Mail, MailCheck, Clock, AlertCircle, CheckCircle, XCircle, Send, Loader2, RefreshCw } from 'lucide-react'
import { useEmailMessages, usePendingMessages, useConfirmMessage, useEmailStats, useFetchEmails } from '../../hooks/useEmailConnections'
import type { EmailConnection, EmailMessage, EmailStatus } from '../../types/email'

interface Props {
  connection: EmailConnection
  onClose: () => void
}

const statusConfig: Record<EmailStatus, { label: string; color: string; icon: typeof Mail }> = {
  received: { label: 'Reçu', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', icon: Mail },
  processing: { label: 'Traitement', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', icon: Clock },
  response_generated: { label: 'Réponse générée', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', icon: CheckCircle },
  pending_confirmation: { label: 'En attente', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300', icon: AlertCircle },
  sending: { label: 'Envoi...', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', icon: Send },
  replied: { label: 'Répondu', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', icon: MailCheck },
  ignored: { label: 'Ignoré', color: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400', icon: XCircle },
  discarded: { label: 'Rejeté', color: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400', icon: XCircle },
  reply_failed: { label: 'Échec', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', icon: AlertCircle },
}

type TabType = 'pending' | 'all'

export function EmailMessagesModal({ connection, onClose }: Props) {
  const [activeTab, setActiveTab] = useState<TabType>('pending')
  const [selectedMessage, setSelectedMessage] = useState<EmailMessage | null>(null)
  
  const { data: allMessages, isLoading: loadingAll, refetch: refetchAll } = useEmailMessages(connection.id)
  const { data: pendingMessages, isLoading: loadingPending, refetch: refetchPending } = usePendingMessages(connection.id)
  const { data: stats, refetch: refetchStats } = useEmailStats(connection.id)
  const fetchEmails = useFetchEmails()

  const messages = activeTab === 'pending' ? pendingMessages : allMessages
  const isLoading = activeTab === 'pending' ? loadingPending : loadingAll
  const isFetching = fetchEmails.isPending

  // ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const handleRefresh = async () => {
    try {
      await fetchEmails.mutateAsync(connection.id)
      // Les queries sont automatiquement invalidées par le hook
    } catch (error) {
      console.error('Erreur lors du fetch des emails:', error)
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm" onClick={onClose} aria-hidden />
      
      {/* Side Panel - Larger for messages */}
      <aside className="fixed inset-y-0 right-0 z-[80] w-full max-w-4xl border-l border-emerald-100/80 bg-white/95 shadow-2xl dark:border-emerald-900/40 dark:bg-zinc-900/95">
        <div className="flex h-full flex-col">
          {/* Header */}
          <header className="flex items-start justify-between gap-4 border-b border-emerald-100/80 px-6 py-4 dark:border-emerald-900/40">
            <div className="space-y-1">
              <h2 className="text-xl font-semibold text-zinc-900 dark:text-white flex items-center gap-2">
                <MailCheck className="h-5 w-5 text-emerald-500" />
                Messages
              </h2>
              <p className="text-sm text-zinc-600 dark:text-zinc-300">{connection.email_address}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={isFetching}
                className="rounded-full p-2 text-zinc-500 transition hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800 disabled:opacity-50"
                title="Récupérer les nouveaux emails"
              >
                <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 text-zinc-500 transition hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </header>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-4 gap-3 px-6 py-4 bg-emerald-50/50 border-b border-emerald-100/80 dark:bg-emerald-900/10 dark:border-emerald-900/40">
              <StatCard label="Reçus" value={stats.emails_received} color="blue" />
              <StatCard label="Répondus" value={stats.emails_replied} color="emerald" />
              <StatCard label="En attente" value={stats.emails_pending} color="orange" />
              <StatCard label="Ignorés" value={stats.emails_ignored} color="zinc" />
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 px-6 py-3 border-b border-emerald-100/80 dark:border-emerald-900/40">
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex-1 py-2.5 px-4 rounded-full text-sm font-medium transition-colors ${
                activeTab === 'pending'
                  ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                  : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800'
              }`}
            >
              🔔 En attente ({pendingMessages?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`flex-1 py-2.5 px-4 rounded-full text-sm font-medium transition-colors ${
                activeTab === 'all'
                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                  : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800'
              }`}
            >
              📬 Tous les messages
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden flex">
            {/* Messages List */}
            <div className={`${selectedMessage ? 'w-1/2' : 'w-full'} overflow-y-auto border-r border-emerald-100/60 dark:border-emerald-900/40`}>
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
                </div>
              ) : messages && messages.length > 0 ? (
                <div className="divide-y divide-emerald-100/60 dark:divide-emerald-900/40">
                  {messages.map((message) => (
                    <MessageRow
                      key={message.id}
                      message={message}
                      isSelected={selectedMessage?.id === message.id}
                      onClick={() => setSelectedMessage(message)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100 dark:bg-emerald-900/40">
                    <Mail className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <p className="text-zinc-900 dark:text-white font-medium">
                    {activeTab === 'pending' ? 'Aucun message en attente' : 'Aucun message'}
                  </p>
                </div>
              )}
            </div>

            {/* Message Detail */}
            {selectedMessage && (
              <div className="w-1/2 overflow-y-auto bg-zinc-50/50 dark:bg-zinc-800/30">
                <MessageDetail
                  message={selectedMessage}
                  connectionId={connection.id}
                  onClose={() => setSelectedMessage(null)}
                  onActionComplete={() => {
                    refetchPending()
                    refetchAll()
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  )
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  const colors: Record<string, string> = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
    emerald: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
    zinc: 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400',
  }

  return (
    <div className={`rounded-xl p-3 ${colors[color]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs opacity-80">{label}</div>
    </div>
  )
}

interface MessageRowProps {
  message: EmailMessage
  isSelected: boolean
  onClick: () => void
}

function MessageRow({ message, isSelected, onClick }: MessageRowProps) {
  const config = statusConfig[message.status]
  const StatusIcon = config.icon

  return (
    <div
      onClick={onClick}
      className={`p-4 cursor-pointer transition-colors ${
        isSelected
          ? 'bg-emerald-50/80 dark:bg-emerald-900/20'
          : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/50'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-1.5 rounded-full ${config.color}`}>
          <StatusIcon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-medium text-zinc-900 dark:text-white truncate">
              {message.from_name || message.from_email}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${config.color}`}>
              {config.label}
            </span>
          </div>
          <p className="text-sm text-zinc-900 dark:text-white truncate font-medium">
            {message.subject || '(sans sujet)'}
          </p>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
            {message.body_plain?.slice(0, 100)}...
          </p>
          <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
            {message.received_at && new Date(message.received_at).toLocaleString('fr-FR')}
          </p>
        </div>
      </div>
    </div>
  )
}

interface MessageDetailProps {
  message: EmailMessage
  connectionId: number
  onClose: () => void
  onActionComplete: () => void
}

function MessageDetail({ message, connectionId, onClose, onActionComplete }: MessageDetailProps) {
  const [editedResponse, setEditedResponse] = useState(message.ai_response || '')
  const confirmMessage = useConfirmMessage()

  const handleAction = async (action: 'send' | 'edit' | 'discard') => {
    await confirmMessage.mutateAsync({
      messageId: message.id,
      connectionId,
      action,
      edited_response: action === 'edit' ? editedResponse : undefined,
    })
    onActionComplete()
    if (action !== 'edit') {
      onClose()
    }
  }

  const config = statusConfig[message.status]

  return (
    <div className="p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className={`px-3 py-1 rounded-full text-sm ${config.color}`}>
          {config.label}
        </span>
        <button onClick={onClose} className="p-1.5 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Email Info */}
      <div className="space-y-2 mb-4 text-sm">
        <div>
          <span className="text-zinc-500 dark:text-zinc-400">De:</span>{' '}
          <span className="text-zinc-900 dark:text-white">
            {message.from_name} &lt;{message.from_email}&gt;
          </span>
        </div>
        <div>
          <span className="text-zinc-500 dark:text-zinc-400">Sujet:</span>{' '}
          <span className="text-zinc-900 dark:text-white font-medium">
            {message.subject || '(sans sujet)'}
          </span>
        </div>
        <div>
          <span className="text-zinc-500 dark:text-zinc-400">Date:</span>{' '}
          <span className="text-zinc-900 dark:text-white">
            {message.received_at && new Date(message.received_at).toLocaleString('fr-FR')}
          </span>
        </div>
      </div>

      {/* Original Message */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">Message original</h4>
        <div className="p-3 bg-zinc-100/80 dark:bg-zinc-800/60 rounded-xl text-sm text-zinc-700 dark:text-zinc-300 max-h-40 overflow-y-auto whitespace-pre-wrap">
          {message.body_plain || '(contenu vide)'}
        </div>
      </div>

      {/* AI Response */}
      {message.ai_response && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2 flex items-center gap-2">
            🤖 Réponse IA
            {message.ai_confidence && (
              <span className="text-xs text-zinc-500">
                (confiance: {Math.round(message.ai_confidence * 100)}%)
              </span>
            )}
          </h4>
          {message.status === 'pending_confirmation' ? (
            <textarea
              value={editedResponse}
              onChange={(e) => setEditedResponse(e.target.value)}
              className="w-full p-3 border border-zinc-200 dark:border-zinc-700 rounded-xl bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white text-sm min-h-[120px] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
            />
          ) : (
            <div className="p-3 bg-emerald-50/80 dark:bg-emerald-900/20 rounded-xl text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap">
              {message.ai_response}
            </div>
          )}
        </div>
      )}

      {/* Pattern Matched */}
      {message.pattern_matched && (
        <div className="mb-4 text-sm">
          <span className="text-zinc-500 dark:text-zinc-400">Pattern:</span>{' '}
          <span className="font-mono text-purple-600 dark:text-purple-400">
            {message.pattern_matched}
          </span>
        </div>
      )}

      {/* Error */}
      {message.error_message && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl text-sm text-red-700 dark:text-red-400">
          ⚠️ {message.error_message}
        </div>
      )}

      {/* Actions for pending messages */}
      {message.status === 'pending_confirmation' && (
        <div className="flex gap-2 pt-4 border-t border-zinc-200 dark:border-zinc-700">
          <button
            onClick={() => handleAction('discard')}
            disabled={confirmMessage.isPending}
            className="flex-1 py-2.5 px-4 border border-zinc-200/70 text-zinc-600 dark:border-zinc-700 dark:text-zinc-300 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50 transition"
          >
            Rejeter
          </button>
          <button
            onClick={() => handleAction('edit')}
            disabled={confirmMessage.isPending || editedResponse === message.ai_response}
            className="flex-1 py-2.5 px-4 bg-amber-500 text-white rounded-full hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2 transition"
          >
            {confirmMessage.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Modifier & Envoyer
          </button>
          <button
            onClick={() => handleAction('send')}
            disabled={confirmMessage.isPending}
            className="flex-1 py-2.5 px-4 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 disabled:opacity-50 flex items-center justify-center gap-2 transition"
          >
            {confirmMessage.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Envoyer
          </button>
        </div>
      )}
    </div>
  )
}
